import { CheckCircle, Info } from "@mui/icons-material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import { green, red } from "@mui/material/colors";
import { Link } from "@tanstack/react-router";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { AccountType } from "@/module/accounts-module/accounts-module";
import { ResultDto } from "@/types/common-types";

const BulkOperationReportModal = ({
  title = undefined,
  resultList,
  showSuccess = false,
  showProblematic = false,
  open,
  onCancel,
}: {
  title?: string;
  resultList: ResultDto<any>[];
  showSuccess?: boolean;
  showProblematic?: boolean;
  open?: boolean;
  onCancel: () => void;
}) => {
  const { t } = useTranslation();
  const copyToClipboard = (data: string) => {
    navigator.clipboard.writeText(data).then(() => alert(t("textCopied")));
  };

  const successList = resultList.filter((item) => item.status === "SUCCESS");
  const errorList = resultList.filter((item) => item.status === "ERROR");

  const [successListIsShown, setSuccessListIsShown] = useState<boolean>(false);
  const [errorListIsShown, setErrorListIsShown] = useState<boolean>(false);

  const joinListNamesToString = (data: ResultDto<any>[]) => {
    return data.map((proxy) => proxy.name).join("\n");
  };

  return (
    <Dialog open={!!open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body1">
          <Divider sx={{ marginBottom: 2 }} />

          {showSuccess && successList.length > 0 && (
            <List dense>
              <ListItem
                sx={{
                  pl: 0,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <IconButton
                  onClick={() => setSuccessListIsShown((prev) => !prev)}
                  size="small"
                  sx={{ mr: 1 }}
                >
                  <KeyboardArrowRightIcon
                    sx={{
                      transition: "transform 0.2s",
                      transform: successListIsShown ? "rotate(90deg)" : "rotate(0deg)",
                    }}
                  />
                </IconButton>

                <CheckCircle style={{ color: green[500], marginRight: 8 }} />

                <ListItemText
                  primary={t("successElements", {
                    length:
                      successList[0].name === "Deletion operation"
                        ? successList[0].data
                        : successList.length,
                  })}
                />

                <IconButton
                  onClick={() => copyToClipboard(joinListNamesToString(successList))}
                  size="small"
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </ListItem>

              {successListIsShown &&
                successList.map((item) => (
                  <ListItem key={item.name} sx={{ pl: 7 }}>
                    <ListItemText primary={item.data?.name ?? item.message} />
                  </ListItem>
                ))}
            </List>
          )}

          {showSuccess &&
            showProblematic &&
            successList.length > 0 &&
            errorList.length > 0 && <Divider sx={{ marginTop: 2, marginBottom: 2 }} />}

          {showProblematic && errorList.length > 0 && (
            <List dense>
              <ListItem
                sx={{
                  pl: 0,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <IconButton
                  onClick={() => setErrorListIsShown((prev) => !prev)}
                  size="small"
                  sx={{ mr: 1 }}
                >
                  <KeyboardArrowRightIcon
                    sx={{
                      transition: "transform 0.2s",
                      transform: errorListIsShown ? "rotate(90deg)" : "rotate(0deg)",
                    }}
                  />
                </IconButton>

                <Info style={{ color: red[500], marginRight: 8 }} />

                <ListItemText
                  primary={t("problematicElements", {
                    length: errorList.length,
                  })}
                />

                <IconButton
                  onClick={() => copyToClipboard(joinListNamesToString(errorList))}
                  size="small"
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </ListItem>

              {errorListIsShown &&
                errorList.map((item) => (
                  <ListItem key={item.name} sx={{ pl: 7 }}>
                    <ListItemText
                      secondary={
                        <>
                          {item.message}
                          <br />
                          {item.dataOnError &&
                            Object.entries(item.dataOnError).map(
                              ([key, value]: [string, any]) => (
                                <div key={key}>
                                  <div>
                                    <strong>{t("collectionName")}</strong>{" "}
                                    {value.collectionName}
                                  </div>
                                  <div>
                                    <strong>{t("deletableEntityValue")}</strong>{" "}
                                    {value.foreignKeyValue}
                                  </div>
                                  <div>
                                    <strong>{t("room")}</strong> {value.roomName}
                                  </div>
                                  <div>
                                    <strong>{t("amountOfDependencies")}</strong>{" "}
                                    {value.collectionName === "account" ? (
                                      <Link
                                        to="/accounts/$room/$type"
                                        params={{
                                          room: value.roomName,
                                          type: AccountType.regular,
                                        }}
                                        search={{
                                          [value.foreignKeyFieldName]: value.foreignKeyValue,
                                        }}
                                      >
                                        {value.amountOfDependencies}
                                      </Link>
                                    ) : (
                                      "value.amountOfDependencies"
                                    )}
                                  </div>
                                </div>
                              )
                            )}
                        </>
                      }
                    />
                  </ListItem>
                ))}
            </List>
          )}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="primary">
          {t("close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkOperationReportModal;
